import * as et from '../../../application/EventTypes';
import * as dte from "../../DtEventTypes";

import {AssetsLayer} from './assets/AssetsLayer';
import {LevelsLayer} from './levels/LevelsLayer';
import {SpacesLayer} from './spaces/SpacesLayer';
import {TraceLayer} from "./trace/TraceLayer";
import {createDomLayer, HUD_LAYER} from "./HudLayer";

export class HudLayers {
    #facility;
    #viewer;
    #prefsView;
    #prefsOverrides;
    #mounted;

    layers;

    constructor(viewer, facility, hud) {
        this.#viewer = viewer;
        this.#facility = facility;
        this.#prefsView = {};
        this.#prefsOverrides = {}; // Keeps track of layers states during transition (ex: clustering)
        this.dom = createDomLayer(hud.getDocument(), 'dt-hud-layers');

        this.layers = [
            new TraceLayer(facility, hud, viewer),
            new LevelsLayer(facility, hud, viewer),
            new SpacesLayer(facility, hud, viewer),
            new AssetsLayer(facility, hud, viewer)
        ];
        this.layers.forEach((l) => { this.dom.appendChild(l.dom); });

        this.boundOnIsolationChanged = this.#onIsolationChanged.bind(this);
        this.boundOnFacetsUpdated = this.#onFacetsUpdated.bind(this);
        this.boundOnFacetsLoaded = this.#onFacetsLoaded.bind(this);
        this.boundOnHiddenChanged = this.#onHiddenChanged.bind(this);
        this.boundOnModelChanged = this.#onModelChanged.bind(this);
        this.boundCameraChangedEvent = this.#onCameraChanged.bind(this);
        this.boundOnViewChanged = this.#onViewChanged.bind(this);

        // Temporary
        this.boundOnClusterTransition = this.#onClusterTransition.bind(this);
    }

    dispose() {
        this.#viewer.removeEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
        this.#viewer.removeEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnHiddenChanged);
        this.#viewer.removeEventListener(et.CAMERA_CHANGE_EVENT, this.boundCameraChangedEvent);
        this.#facility.eventTarget.removeEventListener(dte.DT_FACETS_UPDATED, this.boundOnFacetsUpdated);
        this.#facility.eventTarget.removeEventListener(dte.DT_FACETS_LOADED, this.boundOnFacetsLoaded);
        this.#facility.eventTarget.removeEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
        this.#facility.eventTarget.views.removeEventListener(dte.DT_CURRENT_VIEW_CHANGED_EVENT, this.boundOnViewChanged);

        // TEMPORARY hide layers while clustering.
        this.#viewer.removeEventListener(et.TRANSITION_STARTED, this.boundOnClusterTransition);
        this.#viewer.removeEventListener(et.TRANSITION_ENDED, this.boundOnClusterTransition);

        this.layers.forEach((l) => l.dispose());

        this.#prefsView = {};
        this.#prefsOverrides = {};
        this.#mounted = false;
    }

    init(viewer) {
        this.#viewer = viewer;
        this.#viewer.addEventListener(et.AGGREGATE_ISOLATION_CHANGED_EVENT, this.boundOnIsolationChanged);
        this.#viewer.addEventListener(et.AGGREGATE_HIDDEN_CHANGED_EVENT, this.boundOnHiddenChanged);
        this.#viewer.addEventListener(et.CAMERA_CHANGE_EVENT, this.boundCameraChangedEvent);
        this.#facility.eventTarget.addEventListener(dte.DT_FACETS_UPDATED, this.boundOnFacetsUpdated);
        this.#facility.eventTarget.addEventListener(dte.DT_FACETS_LOADED, this.boundOnFacetsLoaded);
        this.#facility.eventTarget.addEventListener(dte.DT_MODEL_CHANGED_EVENT, this.boundOnModelChanged);
        this.#facility.eventTarget.views.addEventListener(dte.DT_CURRENT_VIEW_CHANGED_EVENT, this.boundOnViewChanged);

        // TEMPORARY hide layers while clustering.
        this.#viewer.addEventListener(et.TRANSITION_STARTED, this.boundOnClusterTransition);
        this.#viewer.addEventListener(et.TRANSITION_ENDED, this.boundOnClusterTransition);

        this.layers.forEach((l) => l.init());

        this.#mounted = true;
        return this;
    }

    /** Force invalidate all layers */
    invalidate() {
        this.layers.forEach((l) => l.invalidate());
    }

    getLayer(id) {
        for (const l of this.layers)
            if (l.layerID === id)
                return l;

        return null;
    }

    setLayerVisibility(layers, visible) {
        layers = Array.isArray(layers) ? layers : [layers];

        for (const l of layers) {
            let layer;
            if (typeof l === "string") {
                layer = this.getLayer(l);
            } else {
                layer = l;
            }
            // Using isOn instead of isVisible to compare against "final" rather than animated visibility.
            if (layer.isOn() !== visible) {
                layer.setVisible(visible);
            }
        }
    }

    update(ts) {
        for (const layer of this.layers) {
            if (!layer.isDirty) continue;
            layer.update(ts);
        }

        // TODO implement label overlap cleanup.
        //this.#priorityBasedCleanup();
    }

    #onClusterTransition({ type }) {
        const isClustered = !!this.#facility.facetsManager.currentClusterFacet;
        for (const layer of this.layers) {
            const prefs = this.getLayerPreference(layer.layerID);
            layer.onClusterTransition(type, prefs, isClustered);
        }
    }

    #onIsolationChanged({ isolation }) {
        this.layers.forEach((l) => l.onIsolationChanged(isolation));
    }

    #onHiddenChanged({ hidden }) {
        this.layers.forEach((l) => l.onHiddenChanged(hidden));
    }

    #onFacetsUpdated(event) {
        this.layers.forEach((l) => l.onFacetsUpdated(event));
    }

    #onFacetsLoaded(event) {
        this.layers.forEach((l) => l.onFacetsLoaded(event));
    }

    #onModelChanged(event) {
        this.layers.forEach((l) => l.onModelChanged(event));
    }

    #onCameraChanged({ camera }) {
        this.layers.forEach((l) => {
            if (l.isVisible()) l.onCameraChanged(camera);
        });
    }

    #onViewChanged({ detail: { view } }) {
        this.#prefsView = view?.hud?.layers || {};
        this.#prefsOverrides = {};

        if (view?.facets.clusteredFacetId) {
            this.layers.forEach((l) => l.onClusterTransition(true));
            // Clustered view, delay layer visibility until TRANSITION_ENDED
            this.setLayerVisibility(this.layers, false);
        } else {
            this.layers.forEach((l) => {
                l.onClusterTransition(false);
                this.setLayerVisibility(l, this.getLayerPreference(l.layerID).enabled);
            });
        }
    }

    getLayerPreference(layerID) {
        return this.#prefsOverrides[layerID] || this.#prefsView[layerID] || HUD_LAYER[layerID].defaultPrefs;
    }

    getStateForView() {
        const state = this.layers.reduce((res, l) => {
            res[l.layerID] = { enabled: l.isOn() };
            return res;
        }, {});

        // Temporary until more settled.
        delete state[HUD_LAYER.ASSETS.id];

        return state;
    }
}
